import { useCallback, useMemo } from "react";
import { createQueryScopedTextFilterGridCache } from "./textFilterGridCache.ts";
import type { TextFilterGridLoadRows } from "./textFilterGridTypes.ts";

interface Params {
  loadRows: TextFilterGridLoadRows;
  queryKey: string;
  cacheScope: readonly unknown[];
  persistent: boolean;
}

const useCachedTextFilterGridLoadRows = ({
  loadRows,
  queryKey,
  cacheScope,
  persistent,
}: Params): TextFilterGridLoadRows => {
  const cacheScopeKey = JSON.stringify(cacheScope);
  const cache = useMemo(() => createQueryScopedTextFilterGridCache(cacheScopeKey), [cacheScopeKey]);

  return useCallback(
    // eslint-disable-next-line @eslint-react/immutability -- the cache is mutable by design and recreated per cacheScopeKey
    async (loadParams) => {
      const query = { queryKey, persistent };
      const cachedResult = cache.get(query, loadParams);
      if (cachedResult !== undefined) {
        return cachedResult;
      }

      const result = await loadRows(loadParams);
      // eslint-disable-next-line @eslint-react/immutability -- see above
      cache.set(query, loadParams, result);
      return result;
    },
    [cache, loadRows, persistent, queryKey]
  );
};

export default useCachedTextFilterGridLoadRows;
